/**
 * Quest analyzer service using Gemini-1.5-Flash
 * This analyzes transcripts to detect mission completion without distracting Gemini Live.
 */

import { TranscriptEntry } from "./store-transcript";

const API_ENDPOINT = "/api/gemini";

export async function analyzeQuests(
    transcripts: TranscriptEntry[],
    missions: string[]
): Promise<number[]> {
    if (transcripts.length === 0 || missions.length === 0) return [];

    // Format last 10-15 messages for context
    const recentTranscripts = transcripts.slice(-15);
    const formattedConversation = recentTranscripts.map((t) => {
        const role = t.role === "ai" ? "Müşteri" : "Satıcı";
        return `${role}: ${t.text}`;
    }).join("\n");

    const missionList = missions.map((m, i) => `${i}: ${m}`).join("\n");

    const prompt = `Sen uzman bir satış eğitmeni ve analizörüsün. Aşağıdaki konuşmayı ve görev listesini analiz et. 
Hangi görevlerin (indeks numarasıyla) konuşma içinde başarıyla tamamlandığını BELİRLE.

GÖREV LİSTESİ:
${missionList}

KONUŞMA:
${formattedConversation}

SADECE tamamlanan görevlerin indeks numaralarını JSON array formatında döndür. 

KRİTİK ANALİZ KURALLARI:
1. HEDEF ODAKLI ANALİZ: Sadece kelime benzerliğine bakma. Görevin asıl amacını (intent) anla. Satıcının (Kullanıcı) o görevi gerçekten yerine getirip getirmediğini sorgula.
2. KESİN AYRIM: Bir konuda soru sormak, o konunun tamamını kapsamaz. Örneğin görev "problemleri sor" ise, sadece yüzeysel bir bilgi sormak (fiyat, adres vb.) bu görevi tamamlamaz. Görevin özündeki "problem/ihtiyaç" odağının yakalanması gerekir.
3. BAĞLAMSAL DOĞRULAMA: Satıcının cümlesi, görevde istenen eylemi/bilgi toplama sürecini gerçekten başlattı mı? Müşteri (AI) bu eylemi/bilgiyi onayladı mı?
4. SIKI DENETİM: Eğer görevin tam olarak karşılandığından şüphen varsa, o indeksi asla ekleme. "Neredeyse tamamlandı" veya "alakalı bir şey sordu" gibi durumlar başarısız sayılır.

SADECE JSON YANIT VER:
Örnek: [0, 2]`;

    try {
        let result: string = "";
        const apiKey = process.env.REACT_APP_GEMINI_API_KEY;

        console.log("📤 Sending transcript to QuestAnalyzer (Last 15 msgs)");

        // Try direct API call (common for local dev)
        if (apiKey) {
            const response = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=${apiKey}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        contents: [{ parts: [{ text: prompt }] }],
                        generationConfig: {
                            temperature: 0,
                            maxOutputTokens: 200,
                        }
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                result = data.candidates?.[0]?.content?.parts?.[0]?.text || "[]";
            } else {
                console.error("❌ QuestAnalyzer Direct API error:", response.status);
            }
        }

        // Fallback to serverless proxy if direct fails or no key
        if (!result) {
            const response = await fetch(API_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ prompt }),
            });
            if (response.ok) {
                const data = await response.json();
                result = data.result || "[]";
            }
        }

        console.log("📥 QuestAnalyzer RAW response:", result);

        // Clean and parse JSON
        const cleanJson = (text: string) => {
            // Remove markdown, and extract anything between []
            let cleaned = text.replace(/```json\n?|\n?```/g, "").trim();
            const startBracket = cleaned.indexOf("[");
            const endBracket = cleaned.lastIndexOf("]");
            if (startBracket !== -1 && endBracket !== -1) {
                cleaned = cleaned.substring(startBracket, endBracket + 1);
            }
            return cleaned;
        };

        try {
            const processed = cleanJson(result);
            const completedIndices = JSON.parse(processed);
            if (Array.isArray(completedIndices)) {
                console.log("🎯 QuestAnalyzer Parsed Indices:", completedIndices);
                return completedIndices.filter(idx => typeof idx === 'number' && idx >= 0 && idx < missions.length);
            }
        } catch (e) {
            console.warn("⚠️ QuestAnalyzer JSON parse failed:", result);
        }

        return [];
    } catch (error) {
        console.error("❌ QuestAnalyzer error:", error);
        return [];
    }
}
